/* eslint-disable no-comments/disallowComments */
import React from 'react';
import { styled } from '@mui/material';

type ResponsiveRowGap = 'none' | 'sm' | 'md' | 'lg';
type ResponsiveRowBreakpoint = 'sm' | 'md' | 'lg';
type ResponsiveRowAlign = 'start' | 'center' | 'end' | 'stretch';
type ResponsiveRowJustify = 'start' | 'center' | 'end' | 'between';

interface ResponsiveRowProps {
    children: React.ReactNode;
    /** Gap between children: `none` (0), `sm` (8px), `md` (16px, default), or `lg` (24px). */
    gap?: ResponsiveRowGap;
    /** Breakpoint at which the row collapses into a column. Defaults to `md`. */
    collapseAt?: ResponsiveRowBreakpoint;
    /** Cross-axis (vertical) alignment in row mode. Defaults to `start`. */
    align?: ResponsiveRowAlign;
    /** Main-axis (horizontal) alignment in row mode. */
    justify?: ResponsiveRowJustify;
    /** Cross-axis (horizontal) alignment when collapsed to column. Defaults to `stretch`. */
    alignWhenStacked?: ResponsiveRowAlign;
    className?: string;
}

const GAP_PX: Record<ResponsiveRowGap, number> = { none: 0, sm: 8, md: 16, lg: 24 };
const ALIGN_VALUE: Record<ResponsiveRowAlign, string> = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    stretch: 'stretch',
};
const JUSTIFY_VALUE: Record<ResponsiveRowJustify, string> = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    between: 'space-between',
};

type StyleProps = {
    $gap: ResponsiveRowGap;
    $collapseAt: ResponsiveRowBreakpoint;
    $align: ResponsiveRowAlign;
    $justify?: ResponsiveRowJustify;
    $alignWhenStacked: ResponsiveRowAlign;
};

const shouldForward = (prop: string) =>
    !['$gap', '$collapseAt', '$align', '$justify', '$alignWhenStacked'].includes(prop);

const StyledRow = styled('div', { shouldForwardProp: (p) => shouldForward(p as string) })<StyleProps>(
    ({ $gap, $collapseAt, $align, $justify, $alignWhenStacked, theme }) => ({
        display: 'flex',
        flexDirection: 'row',
        gap: GAP_PX[$gap],
        alignItems: ALIGN_VALUE[$align],
        justifyContent: $justify ? JUSTIFY_VALUE[$justify] : undefined,
        [theme.breakpoints.down($collapseAt)]: {
            flexDirection: 'column',
            alignItems: ALIGN_VALUE[$alignWhenStacked],
        },
    }),
);

export function ResponsiveRow({
    children,
    gap = 'md',
    collapseAt = 'md',
    align = 'start',
    justify,
    alignWhenStacked = 'stretch',
    className,
}: ResponsiveRowProps) {
    return (
        <StyledRow
            $gap={gap}
            $collapseAt={collapseAt}
            $align={align}
            $justify={justify}
            $alignWhenStacked={alignWhenStacked}
            className={className}
        >
            {children}
        </StyledRow>
    );
}
/* eslint-enable no-comments/disallowComments */
